import React, { useState, useEffect, useMemo } from 'react';
import { Search, Plus, X, ExternalLink } from 'lucide-react';
import { normalizeRelatedLinks, type RelatedLink } from '@shared/related-links';

interface Statistic {
  id: string;
  title: string;
  slug: string;
  category?: string;
}

interface RelatedLinksModalProps {
  selectedLinks: RelatedLink[] | unknown[];
  onSave: (links: RelatedLink[]) => void;
}

const RelatedLinksModal: React.FC<RelatedLinksModalProps> = ({ selectedLinks, onSave }) => {
  const normalizedSelectedLinks = useMemo(
    () => normalizeRelatedLinks(selectedLinks),
    [selectedLinks]
  );
  const [allStatistics, setAllStatistics] = useState<Statistic[]>([]);
  const [filteredStatistics, setFilteredStatistics] = useState<Statistic[]>([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchAllStatistics();
  }, []);

  useEffect(() => {
    filterStatistics();
  }, [searchTerm, allStatistics]);

  const fetchAllStatistics = async () => {
    try {
      setLoading(true);
      const response = await fetch('/api/statistics', {
        credentials: 'include'
      });
      
      if (response.ok) {
        const data = await response.json();
        setAllStatistics(data);
      }
    } catch (error) {
      console.error('Error fetching statistics:', error);
    } finally {
      setLoading(false);
    }
  };

  const filterStatistics = () => {
    if (!searchTerm.trim()) {
      setFilteredStatistics(allStatistics);
      return;
    }

    const filtered = allStatistics.filter(stat => 
      stat.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
      stat.slug.toLowerCase().includes(searchTerm.toLowerCase()) ||
      (stat.category && stat.category.toLowerCase().includes(searchTerm.toLowerCase()))
    );
    setFilteredStatistics(filtered);
  };

  const addLink = (statistic: Statistic) => {
    if (normalizedSelectedLinks.length >= 3) {
      alert('Máximo 3 enlaces relacionados permitidos');
      return;
    }

    const isAlreadySelected = normalizedSelectedLinks.some(
      (link) => link.slug === statistic.slug
    );

    if (!isAlreadySelected) {
      onSave([...normalizedSelectedLinks, { title: statistic.title, slug: statistic.slug }]);
    }
  };

  const removeLink = (slug: string) => {
    onSave(normalizedSelectedLinks.filter((link) => link.slug !== slug));
  };

  const getSelectedStatistic = (link: RelatedLink) => {
    return allStatistics.find((stat) => stat.slug === link.slug);
  };

  return (
    <div className="space-y-6">
      <div>
        <label className="block text-sm font-medium text-foreground mb-2">
          Enlaces Relacionados (Máximo 3)
        </label>
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 mb-4">
          <p className="text-sm text-blue-800">
            🔗 <strong>Instrucciones:</strong> Selecciona hasta 3 artículos relacionados que aparecerán al final del artículo como "También te puede interesar".
          </p>
        </div>
      </div>

      {/* Selected Links */}
      <div>
        <h3 className="text-sm font-medium text-foreground mb-3">Enlaces Seleccionados ({normalizedSelectedLinks.length}/3)</h3>
        <div className="space-y-2">
          {normalizedSelectedLinks.map((link) => {
            const statistic = getSelectedStatistic(link);

            return (
              <div key={link.slug} className="flex items-center justify-between p-3 bg-surface-100 rounded-lg">
                <div className="flex-1">
                  <div className="font-medium text-foreground">
                    {link.title || statistic?.title || link.slug}
                  </div>
                  <div className="text-sm text-muted-foreground">
                    {link.slug}
                  </div>
                  {statistic?.category && (
                    <div className="text-sm text-muted-foreground capitalize">
                      {statistic.category}
                    </div>
                  )}
                </div>
                <button
                  type="button"
                  onClick={() => removeLink(link.slug)}
                  className="p-1 text-red-500 hover:bg-red-50 rounded transition-colors"
                >
                  <X size={16} />
                </button>
              </div>
            );
          })}
          {normalizedSelectedLinks.length === 0 && (
            <div className="text-center text-muted-foreground py-4">
              No hay enlaces seleccionados
            </div>
          )}
        </div>
      </div>

      {/* Search and Add */}
      <div>
        <h3 className="text-sm font-medium text-foreground mb-3">Buscar y Añadir Artículos</h3>
        <div className="relative mb-4">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground" size={16} />
          <input
            type="text"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            placeholder="Buscar por título, slug o categoría..."
            className="w-full pl-10 pr-4 py-2 border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
          />
        </div>

        {loading ? (
          <div className="text-center text-muted-foreground py-8">
            Cargando artículos...
          </div>
        ) : (
          <div className="max-h-64 overflow-y-auto space-y-2">
            {filteredStatistics
              .filter((stat) => !normalizedSelectedLinks.some((link) => link.slug === stat.slug))
              .map((statistic) => (
                <div key={statistic.id} className="flex items-center justify-between p-3 border border-border rounded-lg hover:bg-surface-50 transition-colors">
                  <div className="flex-1">
                    <div className="font-medium text-foreground">
                      {statistic.title}
                    </div>
                    <div className="text-sm text-muted-foreground">
                      {statistic.slug}
                    </div>
                    {statistic.category && (
                      <div className="text-xs text-muted-foreground capitalize">
                        {statistic.category}
                      </div>
                    )}
                  </div>
                  <button
                    type="button"
                    onClick={() => addLink(statistic)}
                    disabled={normalizedSelectedLinks.length >= 3}
                    className="p-2 text-primary hover:bg-primary/10 rounded transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <Plus size={16} />
                  </button>
                </div>
              ))}
            {filteredStatistics.filter(
              (stat) => !normalizedSelectedLinks.some((link) => link.slug === stat.slug)
            ).length === 0 && (
              <div className="text-center text-muted-foreground py-4">
                {searchTerm ? 'No se encontraron artículos' : 'No hay más artículos disponibles'}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default RelatedLinksModal; 